<template>
  <div class="space-y-6">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 mb-1">
        Ingredients to be added to your shopping list
      </h3>
      <p class="text-gray-600 text-sm mb-4">
        Uncheck any ingredients you want to exclude from the shopping list.
      </p>

      <!-- Conflict warning banner -->
      <div v-if="!loading && conflictCount > 0"
        class="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
        <svg class="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <p class="text-sm text-amber-800">
          You have <span class="font-semibold">{{ conflictCount }} {{ conflictCount === 1 ? 'conflict' : 'conflicts' }}</span> in the selected ingredients — some names appear more than once with different quantities or units.
        </p>
      </div>

      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>

      <div v-else-if="ingredients.length === 0" class="text-center py-8 text-gray-500">
        <p>No ingredients found for the selected date range.</p>
      </div>

      <div v-else class="bg-white rounded-lg border border-gray-200 overflow-hidden max-h-96 overflow-y-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-4 py-3 w-10"></th>
              <th class="px-4 py-3 text-left text-sm font-medium text-gray-900">Ingredient</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-gray-900">Quantity</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-gray-900">Unit</th>
              <th class="px-4 py-3 text-left text-sm font-medium text-gray-900">Group</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="ingredient in ingredients"
              :key="ingredient.id"
              class="hover:bg-gray-50 cursor-pointer"
              :class="{ 'opacity-50': !includedIds.has(ingredient.id) }"
              @click="toggleIngredient(ingredient.id)"
            >
              <td class="px-4 py-3">
                <button
                  type="button"
                  class="w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200"
                  :class="includedIds.has(ingredient.id)
                    ? 'bg-primary-500 border-primary-500 hover:bg-primary-600'
                    : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'"
                  @click.stop="toggleIngredient(ingredient.id)"
                >
                  <svg v-if="includedIds.has(ingredient.id)" class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </td>
              <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ ingredient.name }}</td>
              <td class="px-4 py-3 text-sm text-gray-700">{{ ingredient.quantity }}</td>
              <td class="px-4 py-3 text-sm text-gray-700">{{ ingredient.unit }}</td>
              <td class="px-4 py-3 text-sm text-gray-500">{{ ingredient.group?.name || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Navigation buttons -->
    <div class="flex justify-between mt-6">
      <button
        @click="$emit('previous')"
        class="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        Previous
      </button>
      <div class="flex items-center gap-3">
        <!-- Fix Conflicts button — only shown when conflicts exist -->
        <button
          v-if="conflictCount > 0"
          @click="handleNext"
          :disabled="loading"
          class="px-6 py-2 border border-amber-400 text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Fix Conflicts
        </button>
        <button
          @click="conflictCount > 0 ? handleConfirm() : handleNext()"
          :disabled="loading"
          class="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="loading">Creating Shopping List...</span>
          <span v-else>Confirm &amp; Create Shopping List</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'

interface Props {
  ingredients: any[]
  loading: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  previous: []
  next: [excludedIngredientIds: string[]]
  confirm: [excludedIngredientIds: string[]]
}>()

const includedIds = ref(new Set<string>())

watch(() => props.ingredients, (ingredients) => {
  includedIds.value = new Set(ingredients.map(i => i.id))
}, { immediate: true })

const toggleIngredient = (id: string) => {
  if (includedIds.value.has(id)) {
    includedIds.value.delete(id)
  } else {
    includedIds.value.add(id)
  }
  includedIds.value = new Set(includedIds.value)
}

const conflictCount = computed(() => {
  const included = props.ingredients.filter(i => includedIds.value.has(i.id))
  const nameMap = new Map<string, number>()
  for (const item of included) {
    const key = item.name.toLowerCase().trim()
    nameMap.set(key, (nameMap.get(key) ?? 0) + 1)
  }
  return Array.from(nameMap.values()).filter(count => count > 1).length
})

const getExcluded = () =>
  props.ingredients.map(i => i.id).filter(id => !includedIds.value.has(id))

const handleNext = () => {
  emit('next', getExcluded())
}

const handleConfirm = () => {
  emit('confirm', getExcluded())
}
</script>
